/**
 * scanPlate — Claude-only Nigerian plate OCR pipeline.
 *
 * The hybrid Tesseract + Claude pipeline was retired because in-browser
 * Tesseract consistently butchered Nigerian plates (A↔4, S↔5, B↔8 confusions)
 * and forced a second Claude call anyway. We now post the cropped JPEG
 * straight to `/api/ocr/plate`, where Claude Sonnet 4.5 reads the whole image
 * with Nigerian-format context. Results are cached server-side (`ocr_cache`)
 * keyed by SHA-256 of the JPEG, so a repeat scan of the same frame is free.
 *
 * Returns: { plate, engine: "claude" | "claude-cache" | "none", confidence }
 *
 * The second arg (`invertedDataUrl`) is accepted for backward compatibility
 * with the old hybrid signature, but is unused — Claude reads either
 * polarity directly. Callers can keep passing it without harm.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NIGERIAN_FORMAT = /^[A-Z]{3}-(\d{3}[A-Z]{2}|\d{2}[A-Z]{2})$/;

function looksValid(plate) {
  return plate && NIGERIAN_FORMAT.test(plate);
}

export async function scanPlate(processedDataUrl /* , invertedDataUrl */) {
  const stripped = (processedDataUrl || "").replace(/^data:image\/[^;]+;base64,/, "");
  if (!stripped) return { plate: "", engine: "none", confidence: 0 };

  try {
    const r = await axios.post(
      `${API}/ocr/plate`,
      { image_base64: stripped },
      { withCredentials: true, timeout: 15000 },
    );
    const plate = (r.data?.plate || "").toUpperCase();
    if (looksValid(plate)) {
      return {
        plate,
        engine: r.data?.engine || "claude",
        confidence: r.data?.confidence ?? 95,
        image_hash: r.data?.image_hash || "",
      };
    }
    // Claude responded but couldn't read it — surface as a clean miss so
    // the UI prompts the user to retry / type manually.
    return {
      plate: "",
      engine: r.data?.engine || "claude",
      confidence: r.data?.confidence ?? 0,
      image_hash: r.data?.image_hash || "",
    };
  } catch {
    // Network / 502 / timeout — return a clean miss; the UI shows a
    // "couldn't read — try again or type" hint.
    return { plate: "", engine: "none", confidence: 0, image_hash: "" };
  }
}
